// Lib
import { FC, useState } from "react";
// Hooks
import { useViewport } from "hooks";
import { useGetPerksData } from "./hooks/useGetPerksData";
import { useUpdatePerksData } from "./hooks/useUpdatePerksData";
// Types
import { TableAction } from "types/common";
import { CustomerMenuProduct } from "types/customers";
import { Loyalty } from "types/loyalty";
import { DayEnum, ModifiedPerkItem } from "types/healthyPerk";
// Theme
import { theme } from "theme";
// Icons
import { DescriptionBlackIcon, PencilIcon, PlusIcon, TrashIcon } from "icons";
// Components
import { Table, Tabs } from "components";
import { ConfirmDialog, ProductsModal } from "components/Modals";
import { SelectPerkTierModal } from "./components";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";

import { columns, TabNames } from "./config";

export const HealthyPerk: FC = () => {
  const { isDesktop, isMobile } = useViewport();

  const [activeTab, setActiveTab] = useState<DayEnum>(DayEnum.MON);

  const [isDeleteModal, setIsDeleteModal] = useState<ModifiedPerkItem | false>(
    false,
  );
  const [isProductsModal, setIsProductsModal] = useState<boolean>(false);
  const [selectPerkTierModal, setSelectPerkTierModal] = useState<
    ModifiedPerkItem | false
  >(false);

  const { healthyPerksData, modifiedData, isPerksDataLoading } =
    useGetPerksData();

  const {
    updatePerksIsLoading,
    handleUpdatePerk,
    handleCreatePerk,
    handleDeletePerk,
  } = useUpdatePerksData(healthyPerksData);

  const onProductSelect = (product: CustomerMenuProduct) => {
    setSelectPerkTierModal({ product, tiersData: [], tiers: [] });
  };

  const setSelectedTiers = async (perk: ModifiedPerkItem, tiers: Loyalty[]) => {
    if (!perk) return;

    let result = false;

    if (!perk.id) {
      const response = await handleCreatePerk(
        activeTab,
        perk.product,
        tiers.map(t => t.id),
      );

      if (response.result) {
        result = true;
      }
    } else {
      const response = await handleUpdatePerk(
        perk.id,
        activeTab,
        perk.product,
        tiers.map(t => t.id),
      );

      if (response.result) {
        result = true;
      }
    }
    if (result) {
      setSelectPerkTierModal(false);
      setIsProductsModal(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!isDeleteModal) {
      return;
    }

    const { result } = await handleDeletePerk(isDeleteModal.id, activeTab);

    if (result) {
      setIsDeleteModal(false);
    }
  };

  const tableData = modifiedData[activeTab] || [];

  const actions: TableAction[] = [
    {
      title: "",
      Icon: PencilIcon,
      type: "Grey",
      onClick: (row: ModifiedPerkItem) => setSelectPerkTierModal(row),
    },
    {
      title: "",
      Icon: TrashIcon,
      type: "Grey",
      onClick: (row: ModifiedPerkItem) => setIsDeleteModal(row),
    },
  ];

  return (
    <>
      <PageWrapper $fullwidth $column>
        <FlexContainer
          $fullwidth
          $column
          $grow={1}
          $padding={isDesktop && "0 0 32px"}
        >
          <FlexContainer
            $padding="0 0 24px"
            $align="center"
            $justify="space-between"
          >
            <Typography.H1>Healthy Perk</Typography.H1>
          </FlexContainer>

          <Tabs
            activeKey={activeTab}
            items={Object.entries(DayEnum).map(([_key, value]) => ({
              key: value,
              label: TabNames[value],
            }))}
            onChange={(activeKey: DayEnum) => setActiveTab(activeKey)}
          />

          <Table
            isLoading={isPerksDataLoading}
            withPagination={!isDesktop}
            dataSource={tableData || []}
            actions={actions}
            columns={columns}
            empty={{
              icon: DescriptionBlackIcon,
              title: "No Products to show.",
            }}
            header={{
              totalCount: tableData?.length || 0,
              suffix: !isMobile ? (
                <Button.Base
                  type="primary"
                  icon={<PlusIcon fill={theme.color.white} />}
                  onClick={() => setIsProductsModal(true)}
                >
                  Add Product
                </Button.Base>
              ) : (
                <Button.SquaredIcon
                  type="primary"
                  icon={<PlusIcon fill={theme.color.white} />}
                  onClick={() => setIsProductsModal(true)}
                />
              ),
            }}
          />
        </FlexContainer>
      </PageWrapper>

      <ProductsModal
        query={{ perkMenu: true }}
        open={isProductsModal}
        onClose={() => setIsProductsModal(false)}
        onSelect={onProductSelect}
      />

      <SelectPerkTierModal
        perk={selectPerkTierModal}
        isDisabled={updatePerksIsLoading}
        onClose={() => setSelectPerkTierModal(false)}
        onSelect={setSelectedTiers}
      />

      <ConfirmDialog
        open={!!isDeleteModal}
        isLoading={isDeleteModal && updatePerksIsLoading}
        Icon={TrashIcon}
        message={`"${
          (isDeleteModal as ModifiedPerkItem)?.product?.name
        }" will be deleted`}
        description="Are you sure to continue this action?"
        onCancel={() => setIsDeleteModal(false)}
        firstCTAButton={{
          title: "Delete Product",
          status: "danger",
          loading: updatePerksIsLoading,
          onClick: onConfirmDelete,
        }}
      />
    </>
  );
};
